import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Loader2, ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useErpAuth } from "@/context/ErpAuthContext";
import { formatApiErrorDetail } from "@/lib/erpApi";

export default function ErpLogin() {
  const { login } = useErpAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(identifier, password);
      toast.success("Autentificare reușită");
      navigate("/angajati");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Date de autentificare incorecte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1424] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay opacity-50" />
      <div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20"
        style={{ background: "#2563EB" }}
      />
      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Înapoi la site
        </Link>
        <div
          data-testid="erp-login-card"
          className="bg-white rounded-2xl shadow-2xl p-8"
        >
          <div className="flex items-center gap-2.5 mb-6">
            <span className="w-10 h-10 rounded-lg bg-[#2563EB] flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </span>
            <div>
              <p className="font-display font-extrabold text-lg text-[#0B1424] leading-none">
                Sim<span className="text-[#2563EB]">Manager</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">Zona angajați</p>
            </div>
          </div>

          <h1 className="font-display text-2xl font-bold text-[#0B1424] flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#2563EB]" /> Autentificare angajați
          </h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            Acces intern pentru personalul SIMSERV.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="erp-email" className="font-semibold text-[#0B1424]">
                Email sau utilizator
              </Label>
              <Input
                id="erp-email"
                type="text"
                data-testid="erp-input-email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="nume@simserv.ro sau ipopescu"
                className="mt-1.5"
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="erp-password" className="font-semibold text-[#0B1424]">
                Parolă
              </Label>
              <Input
                id="erp-password"
                type="password"
                data-testid="erp-input-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="erp-login-submit-button"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold py-6"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Intră în cont"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
